import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {useTranslation, Trans} from 'react-i18next'
import {
  Box,
  Button,
  CloseButton,
  Divider,
  Flex,
  HStack,
  Text,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
  Image,
  Link,
} from '@chakra-ui/react'
import BackgroundPatternImage from '../../../../assets/images/reward_program/background_pattern_image.svg'
import NumberOneIcon from '../../../../assets/images/reward_program/number_one_icon.svg'
import NumberTwoIcon from '../../../../assets/images/reward_program/number_two_icon.svg'
import NumberThreeIcon from '../../../../assets/images/reward_program/number_three_icon.svg'
import LightningIcon from '../../../../assets/images/reward_program/lightning_icon.svg'
import RefreshIcon from '../../../../assets/images/reward_program/refresh_icon.svg'
import ExclamationIcon from '../../../../assets/images/reward_program/exclamation_icon.svg'
import WalletIcon from '../../../../assets/images/reward_program/wallet_icon.svg'
import AirplaneIcon from '../../../../assets/images/reward_program/airplane_icon.svg'
import BankIcon from '../../../../assets/images/reward_program/bank_icon.svg'
import FAQSection from './FAQSection'

const SupplierRewardsProgramLearnMoreModal = ({isOpen, onClose, size}) => {
  const initialFocusRef = useRef()
  const {t} = useTranslation('points')

  return (
    <Modal
      size={{base: 'full', sm: size}}
      isOpen={isOpen}
      onClose={onClose}
      preserveScrollBarGap
      initialFocusRef={initialFocusRef}
    >
      <ModalOverlay />
      <ModalContent px="6" w="1104px">
        <ModalHeader ref={initialFocusRef} px="0" py="5">
          <HStack justifyContent="space-between" alignItems="center">
            <Text
              textStyle="headline3"
              fontSize="xl"
              fontWeight="bold"
              lineHeight="90%"
              letterSpacing="-0.05em"
            >
              {t('supplierRewardsProgramLearnMoreModal.title')}
            </Text>
            <CloseButton
              onClick={onClose}
              data-testid="close-button"
              size={{base: 'lg', md: 'md'}}
              alignItems={{base: 'center', md: 'center'}}
              pb={0}
              _hover={{bg: 'transparent'}}
            />
          </HStack>
        </ModalHeader>
        <Divider borderColor="mld.neutral.500" />
        <ModalBody pb="md" px="0">
          <Text textStyle="body-copy" fontSize="md" my="4">
            {t('supplierRewardsProgramLearnMoreModal.description')}
          </Text>

          {/* Visual Header Section */}
          <Box
            position="relative"
            sx={{
              borderColor: 'transparent',
            }}
            border="1px solid"
            borderRadius="2px"
            overflow="hidden"
            mb="6"
            minH="252px"
            backgroundColor="mld.primary.400"
          >
            {/* Decorative background elements */}
            <Box
              position="absolute"
              width="100%"
              height="100%"
              backgroundImage={`url(${BackgroundPatternImage})`}
              zIndex={1}
            />
            {/* Three-step process illustration */}
            <Flex
              justify="space-between"
              align="flex-start"
              direction={{base: 'column', md: 'row'}}
              px="4"
              py="8"
              position="relative"
              zIndex={1}
              mx="auto"
              gap="6"
              minH="252px"
              alignItems="center"
            >
              <Flex
                direction="column"
                justify="center"
                align="flex-start"
                gap="4"
                bg="rgba(255, 255, 255, 0.4)"
                backdropFilter="blur(10px)"
                p="6"
                borderRadius="2px"
                flex="1"
                minH="166px"
                minW={{base: 'full', md: '314px'}}
                boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                border="1px solid rgba(255, 255, 255, 0.2)"
              >
                <Flex align="center" gap="4">
                  <Box width="58px" height="86px" flexShrink={0}>
                    <Image
                      src={NumberOneIcon}
                      alt={t(
                        'supplierRewardsProgramLearnMoreModal.steps.pay.title',
                      )}
                      w="58px"
                      h="86px"
                    />
                  </Box>

                  <Flex
                    direction="column"
                    gap="2"
                    textAlign="left"
                    flex="1"
                    alignSelf="flex-start"
                  >
                    <Text
                      textStyle="body-copy-medium"
                      color="mld.neutral.800"
                      fontWeight="bold"
                    >
                      {t(
                        'supplierRewardsProgramLearnMoreModal.steps.pay.title',
                      )}
                    </Text>
                    <Text textStyle="body-small" color="mld.neutral.800">
                      {t(
                        'supplierRewardsProgramLearnMoreModal.steps.pay.description',
                      )}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>

              <Flex
                direction="column"
                justify="center"
                align="flex-start"
                gap="4"
                bg="rgba(255, 255, 255, 0.4)"
                backdropFilter="blur(10px)"
                p="6"
                borderRadius="2px"
                flex="1"
                minH="166px"
                minW={{base: 'full', md: '314px'}}
                boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                border="1px solid rgba(255, 255, 255, 0.2)"
              >
                <Flex align="center" gap="4">
                  <Box width="65px" height="87px" flexShrink={0}>
                    <Image
                      src={NumberTwoIcon}
                      alt={t(
                        'supplierRewardsProgramLearnMoreModal.steps.earn.title',
                      )}
                      w="65px"
                      h="87px"
                    />
                  </Box>

                  <Flex
                    direction="column"
                    gap="2"
                    textAlign="left"
                    flex="1"
                    alignSelf="flex-start"
                  >
                    <Text
                      textStyle="body-copy-medium"
                      color="mld.neutral.800"
                      fontWeight="bold"
                    >
                      {t(
                        'supplierRewardsProgramLearnMoreModal.steps.earn.title',
                      )}
                    </Text>
                    <Text textStyle="body-small" color="mld.neutral.800">
                      {t(
                        'supplierRewardsProgramLearnMoreModal.steps.earn.description',
                      )}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>

              <Flex
                direction="column"
                justify="center"
                align="flex-start"
                gap="4"
                bg="rgba(255, 255, 255, 0.4)"
                backdropFilter="blur(10px)"
                p="6"
                borderRadius="2px"
                flex="1"
                minH="166px"
                minW={{base: 'full', md: '314px'}}
                boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                border="1px solid rgba(255, 255, 255, 0.2)"
              >
                <Flex align="center" gap="4">
                  <Box width="71px" height="92px" flexShrink={0}>
                    <Image
                      src={NumberThreeIcon}
                      alt={t(
                        'supplierRewardsProgramLearnMoreModal.steps.redeem.title',
                      )}
                      w="71px"
                      h="92px"
                    />
                  </Box>

                  <Flex
                    direction="column"
                    gap="2"
                    textAlign="left"
                    flex="1"
                    alignSelf="flex-start"
                  >
                    <Text
                      textStyle="body-copy-medium"
                      color="mld.neutral.800"
                      fontWeight="bold"
                    >
                      {t(
                        'supplierRewardsProgramLearnMoreModal.steps.redeem.title',
                      )}
                    </Text>
                    <Text textStyle="body-small" color="mld.neutral.800">
                      {t(
                        'supplierRewardsProgramLearnMoreModal.steps.redeem.description',
                      )}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Box>
          <Box pt="4">
            <VStack spacing="6" align="stretch" mb="6" gap="14">
              <Box>
                <Text
                  textTransform="uppercase"
                  textStyle="headline3"
                  fontSize={{base: '32px', md: '42px'}}
                  fontWeight="medium"
                  lineHeight="90%"
                  letterSpacing="-0.05em"
                  mb={{base: '4', md: '-0.3rem'}}
                >
                  {t(
                    'supplierRewardsProgramLearnMoreModal.benefits.forYou.title',
                  )}
                </Text>
                <Divider borderColor="mld.neutral.700" />

                <Flex gap="8" direction={{base: 'column', md: 'row'}}>
                  <Box flex="1" display={{base: 'none', md: 'block'}} />
                  <Box flex="1" width={{base: '100%', md: 'auto'}}>
                    <VStack spacing="4" align="stretch" mt="6">
                      <Flex gap="3" align="flex-start">
                        <Box width="24px" height="24px" flexShrink={0}>
                          <Image
                            src={LightningIcon}
                            alt={t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forYou.items.winMoreWork.title',
                            )}
                            w="24px"
                            h="24px"
                          />
                        </Box>
                        <Box>
                          <Text
                            textStyle="body-copy"
                            fontSize="md"
                            textTransform="uppercase"
                            mb="2"
                            mt="-2px"
                          >
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forYou.items.winMoreWork.title',
                            )}
                          </Text>
                          <Text textStyle="body-copy" color="mld.neutral.700">
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forYou.items.winMoreWork.description',
                            )}
                          </Text>
                        </Box>
                      </Flex>

                      <Divider borderColor="mld.neutral.600" />

                      <Flex gap="3" align="flex-start">
                        <Box width="24px" height="24px" flexShrink={0}>
                          <Image
                            src={ExclamationIcon}
                            alt={t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forYou.items.retainLoyalCustomers.title',
                            )}
                            w="24px"
                            h="24px"
                          />
                        </Box>
                        <Box alignSelf="flex-start">
                          <Text
                            textStyle="body-intro"
                            textTransform="uppercase"
                            mb="2"
                            mt="-2px"
                          >
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forYou.items.retainLoyalCustomers.title',
                            )}
                          </Text>
                          <Text textStyle="body-copy" color="mld.neutral.700">
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forYou.items.retainLoyalCustomers.description',
                            )}
                          </Text>
                        </Box>
                      </Flex>

                      <Divider borderColor="mld.neutral.600" />

                      <Flex gap="3" align="flex-start">
                        <Box width="24px" height="24px" flexShrink={0}>
                          <Image
                            src={RefreshIcon}
                            alt={t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forYou.items.offerRealPerks.title',
                            )}
                            w="24px"
                            h="24px"
                          />
                        </Box>
                        <Box>
                          <Text
                            textStyle="body-intro"
                            textTransform="uppercase"
                            mb="2"
                            mt="-2px"
                          >
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forYou.items.offerRealPerks.title',
                            )}
                          </Text>
                          <Text textStyle="body-copy" color="mld.neutral.700">
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forYou.items.offerRealPerks.description',
                            )}
                          </Text>
                        </Box>
                      </Flex>
                    </VStack>
                  </Box>
                </Flex>
              </Box>

              <Box>
                <Text
                  textTransform="uppercase"
                  textStyle="headline3"
                  fontSize={{base: '32px', md: '42px'}}
                  fontWeight="medium"
                  lineHeight="90%"
                  letterSpacing="-0.05em"
                  mb={{base: '4', md: '-0.3rem'}}
                >
                  {t(
                    'supplierRewardsProgramLearnMoreModal.benefits.forCustomers.title',
                  )}
                </Text>
                <Divider borderColor="mld.neutral.700" />

                <Flex gap="8" direction={{base: 'column', md: 'row'}}>
                  <Box flex="1" display={{base: 'none', md: 'block'}} />
                  <Box flex="1" width={{base: '100%', md: 'auto'}}>
                    <VStack spacing="4" align="stretch" mt="6">
                      <Flex gap="3" align="flex-start">
                        <Box width="24px" height="24px" flexShrink={0}>
                          <Image
                            src={WalletIcon}
                            alt={t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forCustomers.items.earnOnPayments.title',
                            )}
                            w="24px"
                            h="24px"
                          />
                        </Box>
                        <Box>
                          <Text
                            textStyle="body-intro"
                            textTransform="uppercase"
                          >
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forCustomers.items.earnOnPayments.title',
                            )}
                          </Text>
                          <Text textStyle="body-copy" color="mld.neutral.700">
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forCustomers.items.earnOnPayments.description',
                            )}
                          </Text>
                        </Box>
                      </Flex>

                      <Divider borderColor="mld.neutral.600" />

                      <Flex gap="3" align="flex-start">
                        <Box width="24px" height="24px" flexShrink={0}>
                          <Image
                            src={BankIcon}
                            alt={t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forCustomers.items.convertToRealValue.title',
                            )}
                            w="24px"
                            h="24px"
                          />
                        </Box>
                        <Box>
                          <Text
                            textStyle="body-intro"
                            textTransform="uppercase"
                            mb="2"
                            mt="-2px"
                          >
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forCustomers.items.convertToRealValue.title',
                            )}
                          </Text>
                          <Text textStyle="body-copy" color="mld.neutral.700">
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forCustomers.items.convertToRealValue.description',
                            )}
                          </Text>
                        </Box>
                      </Flex>

                      <Divider borderColor="mld.neutral.600" />

                      <Flex gap="3" align="flex-start">
                        <Box width="24px" height="24px" flexShrink={0}>
                          <Image
                            src={AirplaneIcon}
                            alt={t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forCustomers.items.chooseRewards.title',
                            )}
                            w="24px"
                            h="24px"
                          />
                        </Box>
                        <Box>
                          <Text
                            textStyle="body-intro"
                            textTransform="uppercase"
                            mb="2"
                            mt="-2px"
                          >
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forCustomers.items.chooseRewards.title',
                            )}
                          </Text>
                          <Text textStyle="body-copy" color="mld.neutral.700">
                            {t(
                              'supplierRewardsProgramLearnMoreModal.benefits.forCustomers.items.chooseRewards.description',
                            )}
                          </Text>
                        </Box>
                      </Flex>
                    </VStack>
                  </Box>
                </Flex>
              </Box>

              <Box>
                {/* FAQ Section */}
                <FAQSection t={t} />

                {/* CTA Section */}
                <Flex flexDirection="column" gap="6" py="6">
                  <Text
                    textStyle="body-intro"
                    color="mld.neutral.800"
                    lineHeight="1.5"
                    letterSpacing="-0.02em"
                  >
                    {t('supplierRewardsProgramLearnMoreModal.cta.description')}
                  </Text>
                  <Flex>
                    <Button
                      as="a"
                      href="/settings/rewards"
                      variant="primary"
                      onClick={onClose}
                      aria-label={t(
                        'supplierRewardsProgramLearnMoreModal.cta.button',
                      )}
                    >
                      {t('supplierRewardsProgramLearnMoreModal.cta.button')}
                    </Button>
                  </Flex>
                </Flex>
                <Flex
                  flexDirection="column"
                  gap="2"
                  textStyle="body-small"
                  color="mld.neutral.700"
                >
                  <Flex>
                    <Text minWidth="20px" flexShrink="0">
                      *
                    </Text>
                    <Text>
                      {t(
                        'supplierRewardsProgramLearnMoreModal.faq.disclaimer1',
                      )}
                    </Text>
                  </Flex>
                  <Flex>
                    <Text minWidth="20px" flexShrink="0">
                      **
                    </Text>
                    <Text>
                      {t(
                        'supplierRewardsProgramLearnMoreModal.faq.disclaimer2',
                      )}
                    </Text>
                  </Flex>
                  <Flex>
                    <Text minWidth="20px" flexShrink="0">
                      ***
                    </Text>
                    <Text>
                      <Trans
                        t={t}
                        i18nKey="supplierRewardsProgramLearnMoreModal.faq.disclaimer3"
                        components={{
                          a: (
                            <Link
                              isExternal
                              variant="primary"
                              color="mld.neutral.700"
                              href="https://www.marmalade.com.au/marmalade-rewards-conversion-partner-terms-and-conditions"
                            />
                          ),
                        }}
                      />
                    </Text>
                  </Flex>
                </Flex>
              </Box>
            </VStack>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

SupplierRewardsProgramLearnMoreModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  size: PropTypes.oneOf([
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
    '2xl',
    '3xl',
    '4xl',
    '5xl',
    '6xl',
    'full',
  ]),
}

SupplierRewardsProgramLearnMoreModal.defaultProps = {
  size: '6xl',
}

export default SupplierRewardsProgramLearnMoreModal
